"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { getHighestCollectionOffersQueryParams } from "@/app/collections/offers/utils"
import { PriceQuote } from "@/app/nft/price-quote/price-quote"
import { NftSelectionMoleculeReatom } from "@/app/nft/sell/selection/nft-selection-molecule.reatom"
import { useEvmServiceFee } from "@/app/sdk/action-molecules/eclipse/fees-molecule"
import { useEvmAcceptCollectionBid } from "@/app/sdk/action-molecules/evm/evm-accept-collection-bid-molecule"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useEffect, useMemo } from "react"
import { CollectionMoleculeReatom } from "../../store/index.reatom"
import {
  useCollectionBlockchain,
  useCollectionNativeCurrency,
  useCollectionWrappedCurrency,
} from "../../store/use-collection"
import {
  NftSellOfferMoleculeReatom,
  SellCollectionNftMoleculeReatom,
} from "../use-collection-nfts-sell.reatom"
import { NftForSellSelector } from "./nft-selector/nft-for-sell-selector"
import { NftSingleSell } from "./nft-single-sell"

interface Props {
  isMultiple?: boolean
}

function useCollectionOffersSync() {
  const { setCollectionOffers } = useMolecule(SellCollectionNftMoleculeReatom)
  const { collectionId } = useMolecule(CollectionMoleculeReatom)
  const bffApiClient = useMolecule(BffApiClientMolecule)

  // Fetch offers once for the current collection (no refetchInterval)
  const offersQuery = useQuery({
    ...getHighestCollectionOffersQueryParams(bffApiClient, collectionId),
  })

  // Set offers to atom once when data arrives
  useEffect(() => {
    if (offersQuery.data) {
      setCollectionOffers(collectionId, {
        isLoading: offersQuery.isLoading,
        data: offersQuery.data,
      })
    }
  }, [
    offersQuery.data,
    offersQuery.isLoading,
    setCollectionOffers,
    collectionId,
  ])
}

export const EvmModernSellDialogContent = reatomComponent<Props>(
  ({ isMultiple }: Props) => {
    // Sync offers for both single and multiple NFT sales
    useCollectionOffersSync()

    const nativeCurrency = useCollectionNativeCurrency()
    const wrappedCurrency = useCollectionWrappedCurrency()
    const { sellPrice } = useMolecule(SellCollectionNftMoleculeReatom)
    const sellPriceValue = sellPrice()
    const blockchain = useCollectionBlockchain()

    const { mutate } = useEvmAcceptCollectionBid()
    const { hasOfferToMatch } = useMolecule(NftSellOfferMoleculeReatom)
    const hasOffer = hasOfferToMatch()

    const safeSellPrice = useMemo(() => {
      const result = sellPriceValue ?? {
        amount: "0",
        currency: wrappedCurrency ?? nativeCurrency,
      }
      return result
    }, [sellPriceValue, wrappedCurrency, nativeCurrency])

    const evmServiceFeeQuery = useEvmServiceFee(blockchain, safeSellPrice)

    const { selectedNftsAtom } = useMolecule(NftSelectionMoleculeReatom)
    const selectedNfts = selectedNftsAtom()
    const [nft] = selectedNfts

    const title = isMultiple ? (
      hasOffer ? (
        <Trans>Select items to sell</Trans>
      ) : (
        <Trans>No offers left</Trans>
      )
    ) : (
      <Trans>Accept Offer</Trans>
    )

    const buttonText = isMultiple ? (
      <Trans>Sell now</Trans>
    ) : (
      <Trans>Accept Offer</Trans>
    )
    const buttonDisabled = sellPriceValue === undefined || nft === undefined
    const buttonType = isMultiple ? "submit" : "button"

    return (
      <form className="flex flex-col gap-y-2">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-center text-lg">{title}</DialogTitle>
        </DialogHeader>

        {isMultiple ? (
          <NftForSellSelector />
        ) : (
          <NftSingleSell nft={nft} sellPrice={safeSellPrice} />
        )}

        {nft?.id !== undefined ? (
          <PriceQuote
            nftId={nft?.id}
            price={safeSellPrice}
            serviceFeeQuery={evmServiceFeeQuery}
          />
        ) : null}

        <DialogClose asChild>
          <Button
            className="flex-grow"
            type="button"
            onClick={() => {
              mutate()
            }}
            disabled={buttonDisabled}
          >
            {buttonText}
          </Button>
        </DialogClose>
      </form>
    )
  },
  "EvmModernSellDialogContent",
)
